import React, { useState, useEffect } from "react";
import "./AddCrewMembersDialog.css";
import { Button, CircularProgress } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import useAirlineAdminApi from "../../../../../services/api/useAirlineAdminApi";
import { useFormData } from "../../../../../helpers/FormDataProvider";
import { CloseOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";

const AddCrewMembersDialog = ({
  isOpen,
  onClose,
  onSave,
  initialCrewMembers = [],
  initialCount = 0,
  isLoading,
  flightPlanId,
}) => {
  const { userData } = useFormData();
  const { searchCommercialAirlineProfile, searchScannedPassengers } =
    useAirlineAdminApi(userData?.isAAUser);
  const [crewMemberCount, setCrewMemberCount] = useState(initialCount || 0);
  const [selectedCrewMembers, setSelectedCrewMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [noDataFound, setNoDataFound] = useState(false);

  // Initialize selected crew members and count from props
  useEffect(() => {
    if (initialCrewMembers && initialCrewMembers.length > 0) {
      setSelectedCrewMembers(initialCrewMembers);
      setCrewMemberCount(initialCrewMembers.length);
    } else {
      setSelectedCrewMembers([]);
      setCrewMemberCount(initialCount || 0);
    }
  }, [initialCrewMembers, initialCount]);

  // Update count when members change - automatically sync count with selected members
  useEffect(() => {
    setCrewMemberCount(selectedCrewMembers.length);
  }, [selectedCrewMembers]);

  // Search mutation
  const searchMutation = useMutation({
    mutationFn: ({ data }) => {
      // Use scanned passengers search if flightPlanId is available, otherwise use commercial profile search
      if (flightPlanId) {
        return searchScannedPassengers(flightPlanId, data);
      }
      return searchCommercialAirlineProfile(data);
    },
    onSuccess: (response) => {
      let results = [];

      // Handle different response structures
      if (response && response.data) {
        // If response.data is an array
        if (Array.isArray(response.data)) {
          results = response.data;
        } else {
          // If response.data is a single object, wrap it in an array
          results = [response.data];
        }
      } else if (response && !response.data && typeof response === "object") {
        // If response itself is the data object
        results = [response];
      }

      // Filter out already selected members
      const filtered = results.filter((member) => {
        const memberId = member.id || member.profileId;
        return !selectedCrewMembers.some(
          (selected) =>
            selected.id === memberId || selected.profileId === memberId
        );
      });

      setSearchResults(filtered);
      setIsSearching(false);
      setNoDataFound(false);
    },
    onError: (error) => {
      setSearchResults([]);
      setIsSearching(false);
      // Check if error is 404 (No data found)
      if (error?.status === 404) {
        setNoDataFound(true);
      } else {
        setNoDataFound(false);
      }
    },
  });

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 3) {
      setSearchResults([]);
      setIsSearching(false);
      setNoDataFound(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsSearching(true);
      searchMutation.mutate({
        data: {
          name: searchQuery,
        },
      });
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleAddCrewMember = (member) => {

    // Handle deadhead crew format (has passengerName and seatNumber from API)
    const isDeadheadCrew = member.passengerName && member.seatNumber;

    // Use the actual ID from the API response - this is what gets sent to the API
    // The ID should come directly from the API response, NOT constructed from name/seatNumber
    const actualId = member.id || member.passengerId || member.profileId;
    
    const crewMemberData = {
      // Use actual ID from API for internal tracking
      id: actualId || (member.passengerName && member.seatNumber ? `${member.passengerName}-${member.seatNumber}` : null),
      name: isDeadheadCrew
        ? member.passengerName
        : `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
          member.name,
      seatNumber: member.seatNumber || null,
      firstName: member.firstName,
      lastName: member.lastName,
      profileId: actualId,
      passengerName: member.passengerName || null,
      // Store the actual ID from API - this is what gets sent to the API (NOT constructed)
      actualId: actualId,
    };

    setSelectedCrewMembers((prev) => {
      const newMembers = [...prev, crewMemberData];
      // Automatically update count when adding
      setCrewMemberCount(newMembers.length);
      return newMembers;
    });
    setSearchResults((prev) =>
      prev.filter((m) => {
        const mId = m.id || m.profileId || `${m.passengerName}-${m.seatNumber}`;
        return mId !== crewMemberData.id;
      })
    );
    setSearchQuery("");
  };

  const handleRemoveCrewMember = (memberId) => {
    setSelectedCrewMembers((prev) => {
      const newMembers = prev.filter((m) => {
        const mId = m.id || m.profileId || `${m.passengerName}-${m.seatNumber}`;
        return mId !== memberId;
      });
      // Automatically update count when removing
      setCrewMemberCount(newMembers.length);
      return newMembers;
    });
  };

  const handleSave = () => {
    // Save all selected members - count is automatically synced with selected members
    const count = selectedCrewMembers.length;
    onSave(selectedCrewMembers, count);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setNoDataFound(false);
  };

  if (!isOpen) return null;

  return (
    <div className="crew-members-modal-overlay">
      <div className="crew-members-modal-content">
        <div className="crew-members-modal-header">
          <h2 className="crew-members-modal-title">Add Crew Members</h2>
          <button className="crew-members-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="crew-members-form">
          <div className="crew-members-input-group">
            <label htmlFor="crewMemberCount">Crew Member Count</label>
            <input
              id="crewMemberCount"
              type="number"
              value={crewMemberCount}
              readOnly
              className="crew-members-input"
              min="0"
              step="1"
            />
          </div>

          <div className="crew-members-input-group">
            <label htmlFor="search">Search</label>
            <div className="crew-members-search-container">
              <SearchOutlined className="crew-members-search-icon" />
              <input
                id="search"
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by name"
                className="crew-members-search-input"
              />
              {searchQuery && (
                <button
                  className="crew-members-clear-search"
                  onClick={handleClearSearch}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Search Results */}
          {searchQuery && searchResults.length > 0 && (
            <div className="crew-members-search-results">
              {searchResults.map((member) => {
                const memberId = member.id || member.profileId;
                const isScannedPassenger =
                  member.passengerName && member.seatNumber;
                const memberName = isScannedPassenger
                  ? member.passengerName
                  : `${member.firstName || ""} ${
                      member.lastName || ""
                    }`.trim() || member.name;
                const memberDisplay = member.seatNumber;
                return (
                  <div key={memberId} className="crew-members-result-item">
                    <span className="crew-members-result-name">
                      {memberName} - {memberDisplay}
                    </span>
                    <button
                      className="crew-members-add-button"
                      onClick={() => handleAddCrewMember(member)}
                    >
                      <PlusOutlined />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* No Data Found Message */}
          {searchQuery && !isSearching && noDataFound && searchResults.length === 0 && (
            <div className="crew-members-no-data">
              No data found
            </div>
          )}

          {/* Selected Crew Members - Always show if there are any */}
          {selectedCrewMembers.length > 0 && (
            <div className="crew-members-selected-list">
              {selectedCrewMembers.map((member) => {
                const memberId = member.id || member.profileId;
                const hasSeatNumber = member.seatNumber || member.passengerName;
                return (
                  <div key={memberId} className="crew-members-selected-item">
                    <div className="crew-members-selected-info">
                      <>
                        <div className="crew-members-selected-name">
                          {member.passengerName || member.name}
                        </div>
                        {member.seatNumber && (
                          <div className="crew-members-selected-seat">
                            {member.seatNumber}
                          </div>
                        )}
                      </>
                    </div>
                    <button
                      className="crew-members-remove-button"
                      onClick={() => handleRemoveCrewMember(memberId)}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {isSearching && (
            <div className="crew-members-loading">
              <CircularProgress size={20} />
            </div>
          )}
        </div>

        <Button
          className="crew-members-save-button"
          variant="contained"
          onClick={!isLoading ? handleSave : null}
          disabled={isLoading}
          endIcon={
            isLoading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default AddCrewMembersDialog;
